import React, { type ReactNode, useRef, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { Dialog, DialogFooter, DialogHeader, Icon } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useTranslation } from "react-i18next";
import { type NodeOrFn, renderNodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";

export type LeavePromptProps = {
	when: boolean;
	title: ReactNode;
	children: ReactNode;
	pathToNotBlock?: string[];
	allowSameRouteNavigation?: boolean;
	showCancelIcon?: boolean;
	variant?: "info" | "warning" | "error";
};

function LeavePromptBare({
	children,
	title,
	variant,
	showCancelIcon = true,
	...forward
}: LeavePromptProps): React.JSX.Element {
	const icon = useMemo(
		() =>
			({
				info: <Icon icon="Icon-full-info" color={themeCSSVars.MessageSeverity_success} size={22} />,
				warning: <Icon icon="Icon-full-alert" color={themeCSSVars.MessageSeverity_warning} size={22} />,
				error: (
					<Icon icon="Icon-full-info" color={themeCSSVars.MessageSeverity_error} size={22} className="rotate-180" />
				),
				none: undefined,
			})[variant ?? "warning"],
		[variant],
	);

	const { t } = useTranslation();

	return (
		<LeavePromptBase {...forward}>
			{({ onCancelNavigation, onContinueNavigation, showPrompt }) => (
				<Dialog
					show={showPrompt}
					onClose={showCancelIcon ? onCancelNavigation : undefined}
					header={<DialogHeader icon={icon}>{title}</DialogHeader>}
					footer={
						<DialogFooter
							primaryAction={
								<Button onClick={onContinueNavigation} palette="primary" data-qualifier="LeavePrompt/Leave">
									{t("LEAVE")}
								</Button>
							}
							neutralAction={
								<Button onClick={onCancelNavigation} palette="tertiary" data-qualifier="LeavePrompt/Stay">
									{t("STAY")}
								</Button>
							}
						/>
					}
				>
					{children}
				</Dialog>
			)}
		</LeavePromptBase>
	);
}

export const LeavePrompt = React.memo(LeavePromptBare) as typeof LeavePromptBare;

export type LeavePromptBaseProps = {
	when: boolean;
	// compared with startsWith
	pathToNotBlock?: string[];
	/**
	 * Let a navigation that stays on the current pathname through without prompting — a page that
	 * keeps state in the query string changes it while the user is editing, and the page does not
	 * unmount, so nothing is discarded and there is nothing to confirm. Off by default: it is the
	 * caller that knows whether its own query params survive their own change.
	 */
	allowSameRouteNavigation?: boolean;
	children: NodeOrFn<{
		showPrompt: boolean;
		onCancelNavigation(): void;
		onContinueNavigation(): void;
	}>;
};

export function LeavePromptBase({
	children,
	when,
	pathToNotBlock,
	allowSameRouteNavigation,
}: LeavePromptBaseProps): React.JSX.Element {
	const history = useHistory();
	const historyRef = useRef(history);

	const [showPrompt, setShowPrompt] = useState(false);
	const [currentPath, setCurrentPath] = useState("");
	const [currentSearch, setCurrentSearch] = useState("");
	const [currentState, setCurrentState] = useState<unknown>({});

	const unregisterPromptRef = useRef<null | (() => void)>(null);
	useEffect(() => {
		if (when) {
			unregisterPromptRef.current = historyRef.current.block((prompt) => {
				// Waved through, and the block left armed: the page is not being left, so the next
				// navigation — the one that does leave it — still has to be confirmed.
				if (allowSameRouteNavigation && prompt.pathname === historyRef.current.location.pathname) {
					return;
				}
				if (pathToNotBlock?.some((p) => `${prompt.pathname}${prompt.search}${prompt.hash}`.startsWith(p))) {
					setCurrentPath(prompt.pathname);
					setCurrentSearch(prompt.search);
					setShowPrompt(false);

					unregisterPromptRef.current?.();
					unregisterPromptRef.current = null;
					return;
				}
				setCurrentPath(prompt.pathname);
				setCurrentSearch(prompt.search);
				setCurrentState(prompt.state);
				setShowPrompt(true);
				return "true";
			});
		} else {
			unregisterPromptRef.current?.();
			unregisterPromptRef.current = null;
		}

		return () => {
			// eslint-disable-next-line react-hooks/exhaustive-deps

			unregisterPromptRef.current?.();
			unregisterPromptRef.current = null;
		};
	}, [pathToNotBlock, when, allowSameRouteNavigation]);

	const handleOK = useCallback(() => {
		setShowPrompt(false);

		unregisterPromptRef.current?.();
		unregisterPromptRef.current = null;
		historyRef.current.push(currentPath + currentSearch, currentState);
	}, [currentPath, currentSearch, currentState]);

	const handleCancel = useCallback(() => {
		setShowPrompt(false);
	}, []);

	return (
		<>
			{renderNodeOrFn(children, {
				showPrompt,
				onCancelNavigation: handleCancel,
				onContinueNavigation: handleOK,
			})}
		</>
	);
}
